import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { api } from '../../../context/AuthContext';
import { cn } from '../../../lib/utils';
import { BarChart3, Edit2, Trash2, ExternalLink, Play, Pause, Unlink, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const statusClass = (status) =>
  status === 'active' ? 'bg-green-100 text-green-700'
    : status === 'frozen' ? 'bg-blue-100 text-blue-700'
    : status === 'retired' ? 'bg-gray-100 text-gray-500'
    : 'bg-gray-100 text-gray-700';

const offeringClass = (status) =>
  status === 'open' ? 'bg-emerald-100 text-emerald-700'
    : status === 'closed' ? 'bg-slate-200 text-slate-700'
    : 'bg-amber-100 text-amber-700';

const money = (value, currency = 'EUR') =>
  `${currency} ${(parseFloat(value || 0) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export const useInstrumentActions = (onRefresh) => {
  const setStatus = async (instrumentId, status, confirmMessage) => {
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    try {
      await api.post(`/admin/projects/instruments/${instrumentId}/status`, { status });
      toast.success(`Instrument ${status === 'active' ? 'published' : status}`);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const remove = async (instrumentId) => {
    if (!window.confirm('Are you sure you want to delete this instrument? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/projects/instruments/${instrumentId}`);
      toast.success('Instrument deleted');
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete instrument');
    }
  };

  return { setStatus, remove };
};

/**
 * Canonical instrument card. Used on the Instruments page and inside
 * Project > Instruments so both views always show the same data points.
 */
export const InstrumentCard = ({ instrument, projectName, onEdit, onRefresh, onUnlink }) => {
  const navigate = useNavigate();
  const { setStatus, remove } = useInstrumentActions(onRefresh);

  const progress = instrument.funding_progress || 0;
  const currency = instrument.currency || 'EUR';
  const breakdown = instrument.subscription_breakdown || {};
  const allocatedUnits = breakdown.allocated?.units || 0;
  const pendingUnits = breakdown.pending?.units || 0;

  return (
    <Card
      className="bg-white p-5 rounded-xl border border-black/[0.04] hover:border-primary/50 transition-colors"
      data-testid={`instrument-card-${instrument.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="cursor-pointer" onClick={() => onEdit?.(instrument)}>
          <span className="font-mono text-xs text-foreground-muted">{instrument.code}</span>
          <h4 className="font-medium mt-1">{instrument.name}</h4>
        </div>
        <div className="flex items-center gap-1">
          <Badge variant="outline" className="text-xs capitalize">{instrument.security_class}</Badge>
          <Badge className={cn('text-xs', statusClass(instrument.status))}>{instrument.status || 'draft'}</Badge>
          <Badge className={cn('text-xs capitalize', offeringClass(instrument.offering_status))}>
            {instrument.offering_status || 'draft'}
          </Badge>
          {onUnlink && (
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-foreground-muted hover:text-red-600 hover:bg-red-50"
              onClick={(e) => { e.stopPropagation(); onUnlink(instrument.id); }}
              data-testid={`unlink-instrument-${instrument.id}`}
              title="Unlink instrument from project"
            >
              <Unlink className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {projectName && (
        <div className="mb-3 p-2 bg-slate-50 rounded-lg text-sm">
          <span className="text-foreground-muted">Linked to: </span>
          <button
            className="text-primary hover:underline font-medium"
            onClick={() => navigate(`/admin/projects/${instrument.project_id}`)}
          >
            {projectName}
            <ExternalLink className="h-3 w-3 inline ml-1" />
          </button>
        </div>
      )}

      {/* Funding progress (subscriptions + manual mints, single bar) */}
      <div className="mb-4">
        <div className="flex justify-between text-xs mb-1">
          <span className="text-foreground-muted">Funding Progress</span>
          <span className="font-medium" data-testid={`instrument-progress-${instrument.id}`}>{progress}%</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={cn('h-full rounded-full transition-all duration-500', progress >= 100 ? 'bg-emerald-500' : 'bg-primary')}
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-foreground-muted mt-1">
          <span>{money(instrument.amount_raised, currency)} raised</span>
          <span>of {money(instrument.funding_target, currency)}</span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-sm mb-3">
        <div className="bg-slate-50 p-2 rounded text-center">
          <span className="text-foreground-muted block text-xs">Authorized</span>
          <span className="font-semibold">{instrument.total_authorized_units?.toLocaleString() || 0}</span>
        </div>
        <div className="bg-slate-50 p-2 rounded text-center">
          <span className="text-foreground-muted block text-xs">Subscribed</span>
          <span className="font-semibold">{instrument.units_subscribed?.toLocaleString() || 0}</span>
        </div>
        <div className="bg-slate-50 p-2 rounded text-center">
          <span className="text-foreground-muted block text-xs">Available</span>
          <span className="font-semibold text-green-600">{instrument.units_available?.toLocaleString() || 0}</span>
        </div>
      </div>

      {(allocatedUnits > 0 || pendingUnits > 0) && (
        <p className="text-xs text-foreground-muted mb-3">
          {allocatedUnits > 0 && <span className="text-green-600">{allocatedUnits} allocated</span>}
          {allocatedUnits > 0 && pendingUnits > 0 && ' · '}
          {pendingUnits > 0 && <span className="text-amber-600">{pendingUnits} pending</span>}
          {instrument.subscribers_count ? ` · ${instrument.subscribers_count} subscriptions` : ''}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-foreground-muted mb-4">
        <span>Unit Price: {currency} {instrument.unit_price_at_issuance}</span>
        <span>Min: {instrument.min_subscription_units || 1} units</span>
        {instrument.max_subscription_units && <span>Max: {instrument.max_subscription_units} units</span>}
        {instrument.is_frozen && <Badge variant="secondary" className="text-xs">Frozen</Badge>}
      </div>

      {(instrument.subscription_start || instrument.subscription_end) && (
        <div className="flex items-center gap-4 mb-4 text-xs text-foreground-muted">
          {instrument.subscription_start && (
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              Opens {new Date(instrument.subscription_start).toLocaleDateString()}
            </span>
          )}
          {instrument.subscription_end && (
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              Closes {new Date(instrument.subscription_end).toLocaleDateString()}
            </span>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 pt-3 border-t border-black/[0.04]">
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate(`/admin/captable/${instrument.id}`)}
          data-testid={`view-captable-${instrument.id}`}
        >
          <BarChart3 className="h-3 w-3 mr-1" />
          Cap Table
        </Button>
        {instrument.status === 'draft' && (
          <Button
            size="sm"
            className="bg-green-600 hover:bg-green-700"
            onClick={() => setStatus(instrument.id, 'active', 'Publish this instrument? It becomes active and can accept subscriptions.')}
            data-testid={`publish-instrument-${instrument.id}`}
          >
            <Play className="h-3 w-3 mr-1" />
            Publish
          </Button>
        )}
        {instrument.status === 'active' && (
          <Button
            size="sm"
            variant="outline"
            className="text-orange-600 border-orange-600 hover:bg-orange-50"
            onClick={() => setStatus(instrument.id, 'frozen', 'Freeze this instrument? No new issuances will be allowed.')}
            data-testid={`freeze-instrument-${instrument.id}`}
          >
            <Pause className="h-3 w-3 mr-1" />
            Freeze
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => onEdit?.(instrument)} data-testid={`edit-instrument-${instrument.id}`}>
          <Edit2 className="h-3 w-3 mr-1" />
          Edit
        </Button>
        {(instrument.units_outstanding === 0 && instrument.units_subscribed === 0) && (
          <Button
            size="sm"
            variant="ghost"
            className="text-red-600 hover:bg-red-50"
            onClick={() => remove(instrument.id)}
            data-testid={`delete-instrument-${instrument.id}`}
          >
            <Trash2 className="h-3 w-3 mr-1" />
            Delete
          </Button>
        )}
      </div>
    </Card>
  );
};

export const InstrumentsTable = ({ instruments, getProjectName, onEdit }) => {
  const navigate = useNavigate();

  return (
    <Card className="bg-white border border-black/[0.04] overflow-hidden" data-testid="instruments-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-foreground-muted">
            <tr>
              <th className="text-left font-medium px-4 py-3">Code</th>
              <th className="text-left font-medium px-4 py-3">Name</th>
              <th className="text-left font-medium px-4 py-3">Project</th>
              <th className="text-left font-medium px-4 py-3">Status</th>
              <th className="text-left font-medium px-4 py-3">Offering</th>
              <th className="text-right font-medium px-4 py-3">Authorized</th>
              <th className="text-right font-medium px-4 py-3">Subscribed</th>
              <th className="text-right font-medium px-4 py-3">Raised</th>
              <th className="text-right font-medium px-4 py-3">Progress</th>
              <th className="text-right font-medium px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {instruments.map((instrument) => (
              <tr
                key={instrument.id}
                className="border-t border-black/[0.04] hover:bg-slate-50/60"
                data-testid={`instrument-row-${instrument.id}`}
              >
                <td className="px-4 py-3 font-mono text-xs">{instrument.code}</td>
                <td className="px-4 py-3 font-medium">{instrument.name}</td>
                <td className="px-4 py-3 text-foreground-muted">{getProjectName?.(instrument.project_id) || '—'}</td>
                <td className="px-4 py-3">
                  <Badge className={cn('text-xs', statusClass(instrument.status))}>{instrument.status || 'draft'}</Badge>
                </td>
                <td className="px-4 py-3">
                  <Badge className={cn('text-xs capitalize', offeringClass(instrument.offering_status))}>
                    {instrument.offering_status || 'draft'}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-right">{instrument.total_authorized_units?.toLocaleString() || 0}</td>
                <td className="px-4 py-3 text-right">{instrument.units_subscribed?.toLocaleString() || 0}</td>
                <td className="px-4 py-3 text-right">{money(instrument.amount_raised, instrument.currency)}</td>
                <td className="px-4 py-3 text-right font-medium">{instrument.funding_progress || 0}%</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate(`/admin/captable/${instrument.id}`)}
                      data-testid={`row-captable-${instrument.id}`}
                    >
                      <BarChart3 className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onEdit?.(instrument)}
                      data-testid={`row-edit-instrument-${instrument.id}`}
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default InstrumentCard;
